"use client";
import React, { useEffect, useMemo, useRef, useState } from "react";

interface Option {
    value: string;
    label: string;
}

interface CustomSelectProps {
    options: Option[];
    value: string;
    onChange: (val: string) => void;
    placeholder?: string;
    leftIcon?: React.ReactNode;
    className?: string;
    error?: string | null;
    clearable?: boolean;
    searchable?: boolean;
    clearOnOpen?: boolean;
}

const CustomSelect: React.FC<CustomSelectProps> = ({
    options,
    value,
    onChange,
    placeholder = "Select",
    leftIcon,
    className = "",
    error,
    clearable = false,
    searchable = true,
    clearOnOpen = false,
}) => {
    const [open, setOpen] = useState(false);
    const [highlight, setHighlight] = useState(-1);
    const [search, setSearch] = useState("");
    const [suppressPlaceholder, setSuppressPlaceholder] = useState(false);

    const containerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const prevValueRef = useRef<string>(value);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
                setOpen(false);
                setSearch("");
                setSuppressPlaceholder(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Reset highlight when options / open / search change
    useEffect(() => {
        setHighlight(-1);
    }, [open, options, search]);

    // Focus input when opening (for searchable mode)
    useEffect(() => {
        if (open && searchable && inputRef.current) {
            inputRef.current.focus();
        }
    }, [open, searchable]);

    // Filter options when searchable
    const filteredOptions = useMemo(() => {
        if (!searchable || !search.trim()) return options;

        const term = search.toLowerCase().trim();
        return options.filter(
            (opt) =>
                opt.label.toLowerCase().includes(term) ||
                opt.value.toLowerCase().includes(term)
        );
    }, [options, search, searchable]);

    // Keyboard navigation – only respond when select is focused
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Only handle keys when the select container (or its children) is focused
            if (!containerRef.current?.contains(document.activeElement)) {
                return;
            }

            if (!open) {
                // Open behaviors when closed (only when focused)
                if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === " ") {
                    e.preventDefault();
                    setOpen(true);
                } else if (searchable && /^[a-zA-Z0-9]$/.test(e.key)) {
                    e.preventDefault();
                    setSearch((s) => s + e.key);
                    setOpen(true);
                }
                return;
            }

            // When dropdown is open
            if (e.key === "ArrowDown") {
                e.preventDefault();
                setHighlight((h) => Math.min(h + 1, filteredOptions.length - 1));
            } else if (e.key === "ArrowUp") {
                e.preventDefault();
                setHighlight((h) => Math.max(h - 1, 0));
            } else if (e.key === "Enter") {
                e.preventDefault();
                if (highlight >= 0 && highlight < filteredOptions.length) {
                    onChange(filteredOptions[highlight].value);
                    setOpen(false);
                    setSearch("");
                    setSuppressPlaceholder(false);
                }
            } else if (e.key === "Escape") {
                e.preventDefault();
                setOpen(false);
                setSearch("");
            } else if (e.key === "Backspace") {
                if (search.length > 0) {
                    setSearch((s) => s.slice(0, -1));
                }
            } else if (searchable && /^[a-zA-Z0-9]$/.test(e.key)) {
                e.preventDefault();
                setSearch((s) => s + e.key);
            }
        };

        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
    }, [open, highlight, filteredOptions, onChange, search, searchable]);

    const selectedOption = useMemo(
        () => options.find((o) => o.value === value),
        [options, value]
    );

    const displayedText = suppressPlaceholder ? "" : (selectedOption?.label ?? "");

    const toggleOpen = () => {
        const willOpen = !open;
        setOpen(willOpen);

        if (willOpen) {
            if (clearOnOpen) {
                prevValueRef.current = value;
                setSuppressPlaceholder(true);
                setSearch("");
            }
            if (searchable) setSearch("");
        } else {
            setSuppressPlaceholder(false);
            setSearch("");
        }
    };

    return (
        <div ref={containerRef} className={`relative ${className}`}>
            {/* Trigger / Display area */}
            <div
                role="combobox"
                aria-expanded={open}
                aria-haspopup="listbox"
                aria-controls="select-options-list"
                tabIndex={0}
                onClick={toggleOpen}
                onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        toggleOpen();
                    }
                }}
                className={`w-full h-12 flex items-center relative border rounded-md px-3 pr-10 cursor-pointer bg-white
          ${error ? "border-red-500" : "border-gray-300"}
          focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400
          ${leftIcon ? "pl-10" : ""}`}
            >
                {leftIcon && (
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none">
                        {leftIcon}
                    </span>
                )}

                <span
                    className={`flex-1 text-left truncate ${displayedText ? "text-gray-900" : "text-gray-400"}`}
                >
                    {displayedText || placeholder}
                </span>

                {clearable && value && !suppressPlaceholder && (
                    <button
                        type="button"
                        aria-label="Clear selection"
                        onClick={(e) => {
                            e.stopPropagation();
                            onChange("");
                            setSuppressPlaceholder(false);
                            setSearch("");
                        }}
                        className="absolute right-8 text-gray-400 hover:text-gray-700 z-10"
                    >
                        ×
                    </button>
                )}

                <span className="absolute right-3 pointer-events-none text-gray-500">▾</span>
            </div>

            {/* Dropdown */}
            {open && (
                <div
                    id="select-options-list"
                    role="listbox"
                    className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg overflow-visible"
                >
                    {/* Search input (only when searchable) */}
                    {searchable && (
                        <div className="p-2 border-b border-gray-200 sticky top-0 bg-white z-10">
                            <input
                                ref={inputRef}
                                type="text"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search..."
                                className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 text-sm"
                                onClick={(e) => e.stopPropagation()}
                                autoComplete="off"
                                aria-autocomplete="list"
                                aria-controls="select-options-list"
                            />
                        </div>
                    )}

                    <ul className="max-h-60 overflow-auto">
                        {filteredOptions.length === 0 ? (
                            <li className="px-4 py-3 text-sm text-gray-500">No options found</li>
                        ) : (
                            filteredOptions.map((opt, idx) => {
                                const isHighlighted = idx === highlight;
                                const isSelected = opt.value === value;

                                return (
                                    <li
                                        key={opt.value}
                                        role="option"
                                        aria-selected={isSelected}
                                        className={`
                      ${isHighlighted ? "bg-blue-100" : ""}
                      ${isSelected ? "font-medium" : ""}
                    `}
                                    >
                                        <button
                                            type="button"
                                            onMouseEnter={() => setHighlight(idx)}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onChange(opt.value);
                                                setOpen(false);
                                                setSearch("");
                                                setSuppressPlaceholder(false);
                                            }}
                                            className="w-full text-left px-4 py-2.5 text-sm hover:bg-blue-100 focus:outline-none cursor-pointer"
                                        >
                                            {opt.label}
                                        </button>
                                    </li>
                                );
                            })
                        )}
                    </ul>
                </div>
            )}

            {error && <p className="mt-1.5 text-sm text-red-600">{error}</p>}
        </div>
    );
};

export default CustomSelect;